import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Clock, ArrowRight } from "lucide-react";
import { usePricing } from "../lib/usePricing";

// Animated modal with the service photo on top and the full detailed
// description below. Background dims + blurs while open.
export const ServiceDetailModal = ({ detail, onClose, onBook }) => {
  const open = !!detail;
  // Use live services (live prices + admin text overrides) instead of static
  // SERVICES so admin edits propagate to the detail modal too.
  const { services: liveServices } = usePricing();
  const svc = detail ? liveServices?.[detail.tier]?.[detail.serviceType] : null;

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && open) onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && svc && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
          onClick={onClose}
          data-testid="service-detail-modal"
        >
          <motion.div
            initial={{ y: 40, opacity: 0, scale: 0.94 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 30, opacity: 0, scale: 0.96 }}
            transition={{ type: "spring", damping: 24, stiffness: 240 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-3xl border border-apex-gold/25 shadow-[0_40px_90px_-30px_rgba(229,200,128,0.25)]"
            style={{ background: "var(--uhel-900)" }}
          >
            {/* Photo header */}
            <div className="relative h-60 md:h-72 overflow-hidden">
              <motion.img
                src={svc.image}
                alt={svc.name}
                initial={{ scale: 1.12 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.1, ease: "easeOut" }}
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--uhel-900)] via-[var(--uhel-900)]/30 to-transparent" />
              <button
                onClick={onClose}
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/55 backdrop-blur hover:bg-apex-gold hover:text-apex-bg text-apex-cream flex items-center justify-center transition-colors"
                data-testid="service-detail-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="absolute bottom-4 left-6 right-6">
                <div className="flex items-end justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-xs uppercase tracking-[0.3em] mb-1" style={{ color: "var(--stribro-text)" }}>
                      {detail.tier === "classic" ? "Řada Classic" : "Řada Premium"}
                    </div>
                    <h3 className="font-display text-3xl md:text-4xl font-bold text-apex-cream">{svc.name}</h3>
                  </div>
                  <div
                    className="px-4 py-2 rounded-2xl font-display font-bold text-xl shadow-lg"
                    style={{ background: "var(--krem-tmava)", color: "#000000" }}
                  >
                    {svc.price.toLocaleString("cs-CZ")} Kč
                  </div>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 md:p-8">
              <div className="flex items-center gap-4 text-sm text-apex-cream/70 mb-5">
                <span className="inline-flex items-center gap-1.5"><Clock className="w-5 h-5" style={{ color: "var(--stribro-text)" }} /> {svc.duration} min</span>
                <span className="italic text-apex-cream/80">„{svc.tagline}“</span>
              </div>
              <div className="text-xs uppercase tracking-[0.25em] mb-3" style={{ color: "var(--stribro-text)" }}>Co vše služba obsahuje</div>
              <ul className="grid sm:grid-cols-1 gap-2.5">
                {svc.detailFeatures.map((f, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.15 + i * 0.05, duration: 0.35 }}
                    className="flex items-start gap-2.5 text-sm text-apex-cream/90"
                  >
                    <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: "rgba(199,184,150,0.18)" }}>
                      <Check className="w-3 h-3" style={{ color: "var(--krem-tmava)" }} />
                    </span>
                    <span>{f}</span>
                  </motion.li>
                ))}
              </ul>
              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => { onClose?.(); onBook?.({ tier: detail.tier, serviceType: detail.serviceType }); }}
                  className="btn-cream flex-1 !justify-center"
                  data-testid="service-detail-book-btn"
                >
                  Rezervovat tuto službu <ArrowRight className="w-4 h-4" />
                </button>
                <button onClick={onClose} className="btn-ghost-gold !justify-center" data-testid="service-detail-back-btn">
                  Zpět na ceník
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
